import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ChefHat, Users, Store, CreditCard, Search,
  LogOut, ArrowLeft, Crown, Building2, Mail, Lightbulb
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [usersRes, restRes, suggRes] = await Promise.all([
          fetch(`${API}/super-admin/users`, { headers }),
          fetch(`${API}/super-admin/restaurants`, { headers }),
          fetch(`${API}/assistant/suggestions/all`, { headers }),
        ]);
        if (usersRes.ok) setUsers(await usersRes.json());
        if (restRes.ok) setRestaurants(await restRes.json());
        if (suggRes.ok) setSuggestions(await suggRes.json());
      } catch (err) {
        console.error('Failed to load super admin data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredRestaurants = restaurants.filter(r =>
    r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.plan?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const planColor = (plan) => {
    switch (plan) {
      case 'pro': return 'bg-[#D4A853]/15 text-[#D4A853]';
      case 'enterprise': return 'bg-[#0A422D]/20 text-[#2D6A4F]';
      case 'trial': return 'bg-[#7C3AED]/15 text-[#7C3AED]';
      default: return 'bg-[#333] text-[#999]';
    }
  };

  const roleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-[#EF4444]/15 text-[#EF4444]';
      case 'owner': return 'bg-[#D4A853]/15 text-[#D4A853]';
      default: return 'bg-[#333] text-[#999]';
    }
  };

  const stats = {
    totalUsers: users.length,
    totalRestaurants: restaurants.length,
    proPlans: restaurants.filter(r => r.plan === 'pro').length,
    trialPlans: restaurants.filter(r => r.plan === 'trial').length,
  };

  if (!user || user.role !== 'super_admin') return null;

  return (
    <div className="min-h-screen bg-[#0C0C0C] text-white" data-testid="super-admin-page">
      {/* Header */}
      <header className="border-b border-[#1E1E1E] bg-[#0C0C0C]/95 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-[#666] hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-[#EF4444]" />
              <span className="font-bold text-lg">Super Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#666]">{user.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="text-[#666] hover:text-white" data-testid="sa-logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Utilisateurs', value: stats.totalUsers, icon: Users, color: '#D4A853' },
            { label: 'Restaurants', value: stats.totalRestaurants, icon: Building2, color: '#2D6A4F' },
            { label: 'Plans Pro', value: stats.proPlans, icon: Crown, color: '#E16833' },
            { label: 'En essai', value: stats.trialPlans, icon: CreditCard, color: '#7C3AED' },
          ].map((s, i) => (
            <div key={i} className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-5" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
              <div className="flex items-center gap-2 mb-2">
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
                <span className="text-xs text-[#888]">{s.label}</span>
              </div>
              <p className="text-2xl font-black" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex gap-1 bg-[#111] rounded-xl p-1 border border-[#1E1E1E]">
            {[
              { key: 'users', label: 'Utilisateurs', icon: Users },
              { key: 'restaurants', label: 'Restaurants', icon: Store },
              { key: 'suggestions', label: 'Suggestions', icon: Lightbulb, badge: suggestions.filter(s => s.status === 'new').length },
            ].map(tb => (
              <button key={tb.key}
                onClick={() => setTab(tb.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  tab === tb.key ? 'bg-[#D4A853] text-black' : 'text-[#888] hover:text-white'
                }`}
                data-testid={`tab-${tb.key}`}>
                <tb.icon className="w-4 h-4" />
                {tb.label}
                {tb.badge > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{tb.badge}</span>}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              className="w-full pl-10 pr-4 py-2.5 bg-[#111] border border-[#1E1E1E] rounded-xl text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#D4A853]/50"
              data-testid="sa-search"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4A853]/30 border-t-[#D4A853] rounded-full animate-spin" />
          </div>
        ) : tab === 'users' ? (
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden" data-testid="users-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1E1E1E]">
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Nom</th>
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Email</th>
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Role</th>
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Restaurant</th>
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Inscription</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-10 text-[#666] text-sm">Aucun utilisateur</td></tr>
                  ) : filteredUsers.map((u, i) => (
                    <tr key={i} className="border-b border-[#1E1E1E]/50 hover:bg-[#1A1A1A] transition-colors" data-testid={`user-row-${i}`}>
                      <td className="px-5 py-3.5 text-sm font-medium text-white">{u.name || '-'}</td>
                      <td className="px-5 py-3.5 text-sm text-[#CCC]">
                        <span className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-[#666]" />
                          {u.email}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${roleColor(u.role)}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-[#CCC]">{u.restaurant_name || '-'}</td>
                      <td className="px-5 py-3.5 text-xs text-[#888]">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden" data-testid="restaurants-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1E1E1E]">
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Restaurant</th>
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Plan</th>
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Devise</th>
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Proprietaire</th>
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Creation</th>
                    <th className="text-left text-xs text-[#888] font-medium px-5 py-3">Fin essai</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRestaurants.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-10 text-[#666] text-sm">Aucun restaurant</td></tr>
                  ) : filteredRestaurants.map((r, i) => (
                    <tr key={i} className="border-b border-[#1E1E1E]/50 hover:bg-[#1A1A1A] transition-colors" data-testid={`restaurant-row-${i}`}>
                      <td className="px-5 py-3.5">
                        <span className="flex items-center gap-2 text-sm font-medium text-white">
                          <Building2 className="w-4 h-4 text-[#D4A853]" />
                          {r.name}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${planColor(r.plan)}`}>
                          {r.plan}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-[#CCC]">{r.currency || 'THB'}</td>
                      <td className="px-5 py-3.5 text-sm text-[#CCC]">{r.owner_email || '-'}</td>
                      <td className="px-5 py-3.5 text-xs text-[#888]">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="px-5 py-3.5 text-xs text-[#888]">
                        {r.trial_ends_at ? new Date(r.trial_ends_at).toLocaleDateString('fr-FR') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Suggestions Tab */}
        {tab === 'suggestions' && (
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden" data-testid="suggestions-tab">
            <div className="px-5 py-4 border-b border-[#1E1E1E] flex items-center justify-between">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-[#D4A853]" /> Suggestions clients
              </h2>
              <span className="text-xs text-[#888]">{suggestions.length} suggestion(s)</span>
            </div>
            {suggestions.length === 0 ? (
              <div className="p-10 text-center text-[#888] text-sm">Aucune suggestion pour le moment</div>
            ) : (
              <div className="divide-y divide-[#1E1E1E]">
                {suggestions.map(s => (
                  <div key={s.id} className="px-5 py-4" data-testid={`suggestion-${s.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            s.status === 'new' ? 'bg-blue-500/15 text-blue-400' :
                            s.status === 'in_progress' ? 'bg-[#D4A853]/15 text-[#D4A853]' :
                            s.status === 'done' ? 'bg-green-500/15 text-green-400' :
                            'bg-[#333] text-[#888]'
                          }`}>{s.status}</span>
                          <span className="text-[10px] text-[#666]">{s.restaurant_name || 'N/A'} — {s.user_email}</span>
                        </div>
                        <p className="font-semibold text-white text-sm">{s.title}</p>
                        {s.description && <p className="text-xs text-[#AAA] mt-1">{s.description}</p>}
                        <p className="text-[10px] text-[#555] mt-2">{s.created_at ? new Date(s.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {['new', 'in_progress', 'done', 'rejected'].map(st => (
                          <button key={st} onClick={async () => {
                            try {
                              await fetch(`${API}/assistant/suggestions/${s.id}`, {
                                method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ status: st })
                              });
                              setSuggestions(prev => prev.map(x => x.id === s.id ? { ...x, status: st } : x));
                            } catch {}
                          }}
                            className={`px-2 py-1 rounded-md text-[9px] font-medium transition-colors ${
                              s.status === st ? 'bg-[#D4A853] text-[#0C0C0C]' : 'bg-[#1A1A1A] text-[#888] hover:text-white'
                            }`}>
                            {st === 'new' ? 'Nouveau' : st === 'in_progress' ? 'En cours' : st === 'done' ? 'Fait' : 'Rejeté'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
